import React, { useState } from 'react'
import { Show } from '@refinedev/antd'
import { useShow, useInvalidate } from '@refinedev/core'
import { useNavigate } from 'react-router'
import { App, Button, Card, Col, Divider, Form, Input, Modal, Row, Space, Typography } from 'antd'
import { brand } from '../../theme/brand'
import { Pill } from '../../theme/listkit'
import { supabaseClient } from '../../lib/supabaseClient'
import { fnErrorMessage } from '../../lib/fnError'
import { statusTone, type ClassYearChangeRequest } from './list'

// `id` is what "View full profile" navigates with: /profiles/show/:id resolves
// profiles.id, and this row's user_id is profiles.user_id — a different value
// in every row, so linking with it opens an empty profile.
const SELECT =
  '*, requester:profiles!class_year_change_requests_user_profile_fkey(id,name,avatar_url,class_year)'

const headStyle: React.CSSProperties = {
  fontFamily: brand.fDisplay,
  fontWeight: 700,
  color: brand.ink,
}

const labelStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 4,
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13.5, color: brand.ink }}>{children}</div>
    </div>
  )
}

/**
 * Class-year change request — detail + decision surface.
 *
 * Decisions are NOT a row edit. Approving has side effects the database alone
 * cannot do (apply the profile change atomically, move the member's Stream class
 * chat, notify them, write the audit trail), so both buttons call the
 * class-year-request-decide Edge Function. The table deliberately has no admin
 * UPDATE policy, so a stray useUpdate here would fail rather than silently skip
 * the cascade.
 *
 * Two states the admin must be able to see at a glance:
 *   • the year the member had WHEN THEY ASKED (snapshot on the row) vs their year
 *     NOW (live, from the profile embed) — if someone edited the profile in the
 *     meantime the request may be stale, so we flag it instead of hiding it.
 *   • whether the Stream class-chat move succeeded — the function reports it, and
 *     a failure points the admin at Class Chats → Resync rather than failing the
 *     decision (which has already committed).
 */
export function ClassYearChangeRequestShow() {
  const { query } = useShow<ClassYearChangeRequest>({ meta: { select: SELECT } })
  const { data, isLoading } = query
  const record = data?.data

  const { message } = App.useApp()
  const invalidate = useInvalidate()
  const navigate = useNavigate()

  const [busy, setBusy] = useState<null | 'approve' | 'reject'>(null)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectForm] = Form.useForm<{ note?: string }>()

  const isPending = record?.status === 'pending'
  const liveYear = record?.requester?.class_year ?? null
  const snapshotYear = record?.current_class_year ?? null
  const drifted = Boolean(record && liveYear !== snapshotYear)
  const profileId = record?.requester?.id

  async function decide(action: 'approve' | 'reject', note?: string) {
    if (!record) return
    setBusy(action)
    try {
      const { data: result, error } = await supabaseClient.functions.invoke(
        'class-year-request-decide',
        { body: { requestId: record.id, action, note: note || undefined } },
      )
      if (error) throw error
      const payload = (result ?? {}) as { error?: string; cascade?: { classChat?: string } }
      if (payload.error) throw new Error(payload.error)

      if (action === 'approve') {
        message.success(
          `Approved — ${record.requester?.name ?? 'the member'} is now Class of ${record.requested_class_year}.`,
        )
        if (payload.cascade?.classChat === 'failed') {
          message.warning(
            'The class year was updated, but their class chat could not be moved. Run Community → Chats → Class Chats → Resync.',
            8,
          )
        }
      } else {
        message.success('Request declined — the member has been notified.')
      }
      setRejectOpen(false)
      rejectForm.resetFields()
      invalidate({ resource: 'class_year_change_requests', invalidates: ['list', 'detail'], id: record.id })
      query.refetch()
    } catch (e) {
      message.error(await fnErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  function confirmApprove() {
    if (!record) return
    Modal.confirm({
      title: 'Approve this class year change?',
      content: (
        <div style={{ fontFamily: brand.fSans, fontSize: 13.5 }}>
          <p style={{ marginTop: 8 }}>
            <strong>{record.requester?.name ?? 'This member'}</strong> moves from Class of{' '}
            <strong>{snapshotYear ?? '—'}</strong> to Class of <strong>{record.requested_class_year}</strong>.
          </p>
          <p style={{ color: brand.sub }}>
            This also moves them into the Class of {record.requested_class_year} chat (and out of their old
            one), and updates the directory, class standings, and their profile badge. They will be notified
            in the app.
          </p>
        </div>
      ),
      okText: 'Approve change',
      cancelText: 'Cancel',
      onOk: () => decide('approve'),
    })
  }

  return (
    <Show isLoading={isLoading} title={false} breadcrumb={false} headerButtons={() => null}>
      {/* ── Member hero ─────────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${brand.navy} 0%, ${brand.jaguar} 100%)`,
          borderRadius: 14,
          padding: '22px 24px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '.09em',
              textTransform: 'uppercase',
              color: brand.gold,
              marginBottom: 6,
            }}
          >
            Class year change request
          </div>
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 24, color: '#fff' }}>
            {record?.requester?.name ?? 'Unknown member'}
          </div>
          <Space size={8} style={{ marginTop: 10 }} wrap>
            <Pill tone="neutral">Class of {snapshotYear ?? '—'}</Pill>
            <span style={{ color: brand.gold, fontWeight: 800 }}>→</span>
            <Pill tone="jaguar">Class of {record?.requested_class_year}</Pill>
            {record && <Pill tone={statusTone(record.status)}>{record.status}</Pill>}
          </Space>
        </div>
        {profileId && (
          <Button
            data-testid="cycr-view-profile"
            onClick={() => navigate(`/profiles/show/${profileId}`)}
          >
            View full profile
          </Button>
        )}
      </div>

      {/* ── The request ─────────────────────────────────────────────── */}
      <Card title={<span style={headStyle}>The request</span>} style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Field label="Member">{record?.requester?.name ?? '—'}</Field>
            <Field label="Class year when they asked">{snapshotYear ?? '—'}</Field>
            <Field label="Class year now">
              <Space size={8} wrap>
                <span>{liveYear ?? '—'}</span>
                {drifted && (
                  <Pill tone="gold">changed since the request was filed</Pill>
                )}
              </Space>
            </Field>
          </Col>
          <Col xs={24} md={12}>
            <Field label="Requested class year">{record?.requested_class_year}</Field>
            <Field label="Requested on">
              {record ? new Date(record.created_at).toLocaleString() : '—'}
            </Field>
          </Col>
        </Row>
        <Divider style={{ margin: '4px 0 16px' }} />
        <Field label="Reason given">
          {record?.reason ? (
            <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
              {record.reason}
            </Typography.Paragraph>
          ) : (
            <span style={{ color: brand.sub, fontStyle: 'italic' }}>No reason given.</span>
          )}
        </Field>
        {drifted && (
          <div
            style={{
              marginTop: 4,
              padding: '10px 12px',
              borderRadius: 8,
              background: 'rgba(196,151,49,.08)',
              border: '1px solid rgba(196,151,49,.28)',
              fontFamily: brand.fSans,
              fontSize: 12.5,
              color: brand.ink,
            }}
          >
            Their class year has changed since this request was filed (it was{' '}
            <strong>{snapshotYear ?? '—'}</strong>, now <strong>{liveYear ?? '—'}</strong>). Approving will
            still set it to <strong>{record?.requested_class_year}</strong> — decline instead if this request
            is out of date.
          </div>
        )}
      </Card>

      {/* ── Decision ────────────────────────────────────────────────── */}
      {isPending ? (
        <Card title={<span style={headStyle}>Decision</span>}>
          <p style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub, marginTop: 0 }}>
            Approving updates the member's profile, moves their class chat, and notifies them. Declining
            notifies them too — you can add a note explaining why (they see it in the app; it is not sent in
            the notification itself).
          </p>
          <Space wrap>
            <Button
              type="primary"
              data-testid="cycr-approve"
              loading={busy === 'approve'}
              disabled={busy !== null}
              onClick={confirmApprove}
            >
              Approve change
            </Button>
            <Button
              danger
              data-testid="cycr-reject"
              loading={busy === 'reject'}
              disabled={busy !== null}
              onClick={() => setRejectOpen(true)}
            >
              Decline
            </Button>
          </Space>
        </Card>
      ) : (
        record && (
          <Card title={<span style={headStyle}>Resolution</span>}>
            <Field label="Outcome">
              <Pill tone={statusTone(record.status)}>{record.status}</Pill>
            </Field>
            {record.decision_note && <Field label="Note to the member">{record.decision_note}</Field>}
            <div style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}>
              {record.decided_at
                ? `Reviewed ${new Date(record.decided_at).toLocaleString()}${
                    record.decided_by ? ` by ${record.decided_by.slice(0, 8)}…` : ''
                  }`
                : record.status === 'cancelled'
                  ? 'Withdrawn by the member.'
                  : '—'}
            </div>
          </Card>
        )
      )}

      {/* ── Decline modal (note is OPTIONAL) ────────────────────────── */}
      <Modal
        title="Decline this class year change?"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        okText="Decline request"
        okButtonProps={{ danger: true, loading: busy === 'reject' }}
        onOk={() => {
          const note = rejectForm.getFieldValue('note') as string | undefined
          void decide('reject', note)
        }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="note"
            label="Note to the member (optional)"
            extra="Shown on their request in the app — for example “Our records show 2010; contact the office if that's wrong.”"
          >
            <Input.TextArea rows={3} maxLength={500} data-testid="cycr-reject-note" />
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  )
}
